import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import {
    ExclusiveControlService,
    FEATURE_DJI_LEGACY_CLOUD_SYNC,
    FEATURE_DJI_LOGS,
    FEATURE_DRONE_DEPLOY_LOGS,
    FEATURE_DRONE_HARMONY_LOGS,
    FEATURE_FLIGHT_LOG_AUTOMATIC_MISSION_CONFIGURATION,
    FEATURE_FLYT_BASE_LOGS,
    FEATURE_SKYDIO_LOGS,
    FlightLogCollectionService,
    FlyFreelyError,
    FlyFreelyLoggingService,
    LookupObject,
    OrganisationAutomaticMissionConfigurationDto,
    PersonsOrganisationDto,
    UserService,
    WorkTracker,
    hasAnyPermission,
    hasFeatureFlag
} from '@flyfreely-portal-ui/flyfreely';
import { getOrElse, map } from 'fp-ts/es6/Option';
import { pipe } from 'fp-ts/es6/function';
import { BsModalRef, ModalOptions } from 'ngx-bootstrap/modal';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FlightLogSettingsService } from './flight-log-settings.service';

@Component({
    templateUrl: './flight-log-settings.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    selector: 'flight-log-settings',
    providers: [WorkTracker, FlightLogSettingsService, ExclusiveControlService]
})
export class FlightLogSettings {
    @Input() organisationId: number;

    working = false;
    hasDroneDeploy = false;
    hasDroneHarmony = false;
    hasSkydio = false;
    hasFlytBase = false;
    hasDjiLogs = false;
    hasDjiLegacyCloudSync = false;
    hasMissionConfig = false;

    canManageAll = false;
    canManageOwn = false;

    featureFlags: LookupObject<string[]> = {};

    defaultOrganisationConfig: OrganisationAutomaticMissionConfigurationDto;

    private ngUnsubscribe$ = new Subject<void>();

    screenContext: { [key: string]: string | boolean };

    constructor(
        private modal: BsModalRef,
        private flightLogSettingsService: FlightLogSettingsService,
        private flightLogCollectionService: FlightLogCollectionService,
        private workTracker: WorkTracker,
        private userService: UserService,
        private exclusiveControlService: ExclusiveControlService,
        private logging: FlyFreelyLoggingService,
        modalOptions: ModalOptions
    ) {
        modalOptions.closeInterceptor = () => {
            if (this.exclusiveControlService.locked) {
                this.exclusiveControlService.requestUnlock();
                return Promise.reject();
            }
            return Promise.resolve();
        };
    }

    ngOnInit() {
        this.workTracker.observable
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(working => (this.working = working));

        this.flightLogSettingsService.setOrganisation(this.organisationId);

        this.findPermissions();
        if (this.hasMissionConfig) {
            this.findOrganisationDefaultAutomaticMissionConfigurationSettings();
        }

        this.screenContext = {
            skydio: this.hasSkydio,
            droneDeploy: this.hasDroneDeploy
        };
    }

    ngOnDestroy(): void {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
    }

    findPermissions() {
        const maybeOrganisation = this.userService.findOrganisationForUser(
            this.organisationId
        );

        this.canManageAll = pipe(
            maybeOrganisation,
            map(o =>
                hasAnyPermission(
                    o,
                    PersonsOrganisationDto.Permissions
                        .FLIGHT_LOG_COLLECTION_MANAGE
                )
            ),
            getOrElse(() => false)
        );

        this.canManageOwn = pipe(
            maybeOrganisation,
            map(o =>
                hasAnyPermission(
                    o,
                    PersonsOrganisationDto.Permissions.FLIGHT_LOG_COLLECTION_OWN
                )
            ),
            getOrElse(() => false)
        );

        this.hasDjiLogs = pipe(
            maybeOrganisation,
            map(o => hasFeatureFlag(o, FEATURE_DJI_LOGS)),
            getOrElse(() => false)
        );
        this.featureFlags['hasDjiLogs'] = [FEATURE_DJI_LOGS];

        this.hasDjiLegacyCloudSync = pipe(
            maybeOrganisation,
            map(o => hasFeatureFlag(o, FEATURE_DJI_LEGACY_CLOUD_SYNC)),
            getOrElse(() => false)
        );
        this.featureFlags['hasDjiLegacyCloudSync'] = [
            FEATURE_DJI_LEGACY_CLOUD_SYNC
        ];

        this.hasDroneDeploy = pipe(
            maybeOrganisation,
            map(o => hasFeatureFlag(o, FEATURE_DRONE_DEPLOY_LOGS)),
            getOrElse(() => false)
        );
        this.featureFlags['hasDroneDeploy'] = [FEATURE_DRONE_DEPLOY_LOGS];

        this.hasDroneHarmony = pipe(
            maybeOrganisation,
            map(o => hasFeatureFlag(o, FEATURE_DRONE_HARMONY_LOGS)),
            getOrElse(() => false)
        );
        this.featureFlags['hasDroneHarmony'] = [FEATURE_DRONE_HARMONY_LOGS];

        this.hasSkydio = pipe(
            maybeOrganisation,
            map(o => hasFeatureFlag(o, FEATURE_SKYDIO_LOGS)),
            getOrElse(() => false)
        );
        this.featureFlags['hasSkydio'] = [FEATURE_SKYDIO_LOGS];

        this.hasFlytBase = pipe(
            maybeOrganisation,
            map(o => hasFeatureFlag(o, FEATURE_FLYT_BASE_LOGS)),
            getOrElse(() => false)
        );
        this.featureFlags['hasFlytBase'] = [FEATURE_FLYT_BASE_LOGS];

        this.hasMissionConfig = pipe(
            maybeOrganisation,
            map(o =>
                hasFeatureFlag(
                    o,
                    FEATURE_FLIGHT_LOG_AUTOMATIC_MISSION_CONFIGURATION
                )
            ),
            getOrElse(() => false)
        );
    }

    findOrganisationDefaultAutomaticMissionConfigurationSettings() {
        this.flightLogCollectionService
            .findOrganisationAutomaticMissionConfiguration(this.organisationId)
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe({
                next: config => (this.defaultOrganisationConfig = config),
                error: (error: FlyFreelyError) =>
                    this.logging.error(
                        error,
                        `Error while fetching organisation default automatic mission configuration: ${error.message}`
                    )
            })
            .add(this.workTracker.createTracker());
    }

    close() {
        this.modal.hide();
    }

    showOrganisationSettings() {
        this.flightLogSettingsService.showOrganisationAutomaticMissionConfiguration();
    }
}
